import axios, { AxiosInstance } from 'axios';
import { Task, TaskAttachment, TaskComment, TaskSummary, PagedResponse, TaskStatus } from '../types/Task';
import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

type ApiVersion = 'v1' | 'v2' | 'buggy';

const extractApiErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

/**
 * Unified API Service supporting V1, V2, and Buggy API versions.
 * Automatically adds JWT authentication for V2 endpoints.
 */
class ApiService {
  private apiVersion: ApiVersion = 'v1';

  private createApiInstance(): AxiosInstance {
    const baseURL = `${API_BASE_URL}/${this.apiVersion}/tasks`;
    
    const instance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include JWT token for V2
    instance.interceptors.request.use((config) => {
      if (this.apiVersion === 'v2') {
        const token = authService.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    });

    // Add response interceptor to handle 401 errors and refresh token
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If 401 and haven't retried yet, try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry && this.apiVersion === 'v2') {
          originalRequest._retry = true;

          try {
            await authService.refreshToken();
            const token = authService.getToken();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, user needs to login again
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }

  /**
   * Set the API version to use
   */
  setApiVersion(version: ApiVersion): void {
    this.apiVersion = version;
  }

  /**
   * Get the current API version
   */
  getApiVersion(): ApiVersion {
    return this.apiVersion;
  }

  /**
   * Get all tasks with optional pagination and filtering.
   */
  async getAllTasks(params?: {
    page?: number;
    size?: number;
    sort?: string;
    direction?: 'asc' | 'desc';
    status?: TaskStatus;
    search?: string;
  }): Promise<PagedResponse<Task>> {
    const api = this.createApiInstance();
    const response = await api.get<PagedResponse<Task>>('', { params });
    return response.data;
  }

  /**
   * Get all tasks as a simple array (non-paginated, for simple use cases).
   */
  async getAllTasksSimple(): Promise<Task[]> {
    const api = this.createApiInstance();
    const response = await api.get<PagedResponse<Task>>('', { 
      params: { size: 1000 } 
    });
    return response.data.content;
  }

  /**
   * Get a single task by ID.
   */
  async getTaskById(id: number): Promise<Task> {
    const api = this.createApiInstance();
    const response = await api.get<Task>(`/${id}`);
    return response.data;
  }

  /**
   * Create a new task.
   */
  async createTask(task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>): Promise<Task> {
    const api = this.createApiInstance();
    const response = await api.post<Task>('', task);
    return response.data;
  }

  /**
   * Update an existing task.
   */
  async updateTask(id: number, task: Partial<Task>): Promise<Task> {
    const api = this.createApiInstance();
    const response = await api.put<Task>(`/${id}`, task);
    return response.data;
  }

  async reorderTask(id: number, targetStatus: TaskStatus, targetIndex: number): Promise<Task> {
    const api = this.createApiInstance();
    const response = await api.post<Task>(`/${id}/reorder`, {
      targetStatus,
      targetIndex,
    });
    return response.data;
  }

  async addComment(taskId: number, text: string): Promise<TaskComment> {
    const api = this.createApiInstance();
    const response = await api.post<TaskComment>(`/${taskId}/comments`, { text });
    return response.data;
  }

  async updateComment(taskId: number, commentId: number, text: string): Promise<TaskComment> {
    const api = this.createApiInstance();
    const response = await api.put<TaskComment>(`/${taskId}/comments/${commentId}`, { text });
    return response.data;
  }

  async uploadAttachments(taskId: number, files: File[]): Promise<TaskAttachment[]> {
    const api = this.createApiInstance();
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    try {
      const response = await api.post<TaskAttachment[]>(`/${taskId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Failed to upload attachments.'));
    }
  }

  async deleteAttachment(taskId: number, attachmentId: number): Promise<void> {
    const api = this.createApiInstance();

    try {
      await api.delete(`/${taskId}/attachments/${attachmentId}`);
    } catch (error) {
      throw new Error(extractApiErrorMessage(error, 'Failed to delete attachment.'));
    }
  }

  async deleteComment(taskId: number, commentId: number): Promise<void> {
    const api = this.createApiInstance();
    await api.delete(`/${taskId}/comments/${commentId}`);
  }

  /**
   * Delete a task by ID.
   */
  async deleteTask(id: number): Promise<void> {
    const api = this.createApiInstance();
    await api.delete(`/${id}`);
  }

  /**
   * Delete multiple tasks by IDs.
   */
  async deleteTasks(ids: number[]): Promise<void> {
    const api = this.createApiInstance();
    await Promise.all(ids.map(id => api.delete(`/${id}`)));
  }

  /**
   * Get task statistics (count by status).
   */
  async getTaskSummary(): Promise<TaskSummary> {
    const api = this.createApiInstance();
    const response = await api.get<TaskSummary>('/summary');
    return response.data;
  }

  /**
   * Delete all tasks (admin operation).
   */
  async deleteAllTasks(): Promise<void> {
    const api = this.createApiInstance();
    await api.delete('');
  }
}

// Export singleton instance
export const apiService = new ApiService();
export type { ApiVersion };
